import React, { Suspense, useState } from 'react'
import clsx from 'clsx'

import { useEmojiContext } from 'stream-chat-react'
import { useChannelStateContext, useMessageContext } from 'stream-chat-react'

import type { NimbleEmojiProps } from 'emoji-mart'
import type { ReactionResponse } from 'stream-chat'

import type { DefaultStreamChatGenerics } from '../../types'
import { useProcessReactions } from './hooks/useProcessReactions'
import { ReactionUsersPopup } from './ReactionUsersPopup'

export type SimpleReactionsListProps<
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
> = {
  additionalEmojiProps?: Partial<NimbleEmojiProps>
  handleReaction?: (reactionType: string, event: React.BaseSyntheticEvent) => Promise<void>
  own_reactions?: ReactionResponse<StreamChatGenerics>[]
  reaction_counts?: { [key: string]: number }
  reactions?: ReactionResponse<StreamChatGenerics>[]
}

const UnMemoizedSimpleReactionsList = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>(
  props: SimpleReactionsListProps<StreamChatGenerics>
) => {
  const { handleReaction: propHandleReaction, ...rest } = props

  const { Emoji, emojiConfig } = useEmojiContext('SimpleReactionsList')
  const { handleReaction: contextHandleReaction, message } =
    useMessageContext<StreamChatGenerics>('SimpleReactionsList')
  const { channel } = useChannelStateContext<StreamChatGenerics>('SimpleReactionsList')

  const {
    additionalEmojiProps,
    aggregatedUserNamesByType,
    emojiData,
    emojiByType,
    getEmojiByReactionType,
    iHaveReactedWithReaction,
    latestReactions,
    latestReactionTypes,
    reactionCounts,
  } = useProcessReactions({ emojiConfig, ...rest })

  const [hoveredReactionType, setHoveredReactionType] = useState<string | undefined>(undefined)
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(null)

  const handleReaction = propHandleReaction || contextHandleReaction

  const handleChipClick = (reactionType: string, event: React.BaseSyntheticEvent) => {
    const emoji = emojiByType[reactionType]
    // stream-chat-react's handleReaction only sends { type }; send the emoji too
    // (as the picker does) when adding an encoded emoji_* reaction via its chip.
    if (!propHandleReaction && emoji && !iHaveReactedWithReaction(reactionType)) {
      event.preventDefault()
      channel
        .sendReaction(message.id, { type: reactionType, emoji })
        .catch((error) => console.error('Error adding reaction:', error))
      return
    }
    handleReaction(reactionType, event)
  }

  if (!latestReactions.length) return null

  // Don't filter out reactions - show all reactions including native emojis
  // if (!supportedReactionsArePresent) return null

  // Get the emoji character for the hovered reaction
  const getEmojiCharacter = (reactionType: string) => {
    const isEncodedEmoji = reactionType.startsWith('emoji_')
    if (isEncodedEmoji) {
      return emojiByType[reactionType] || ''
    }
    const isNativeEmoji = reactionType.length <= 4 && !getEmojiByReactionType(reactionType)
    if (isNativeEmoji) {
      return reactionType
    }
    const emojiObject = getEmojiByReactionType(reactionType)
    return emojiObject?.native || emojiObject?.id || ''
  }

  return (
    <div className="flex items-center gap-1">
      <ul
        className="flex items-center gap-1"
        data-testid="simple-reaction-list"
        onMouseLeave={() => {
          setHoveredReactionType(undefined)
          setHoveredElement(null)
        }}
      >
        {latestReactionTypes.map((reactionType, i) => {
          const emojiObject = getEmojiByReactionType(reactionType)
          const isOwnReaction = iHaveReactedWithReaction(reactionType)

          // Check if this is an encoded emoji reaction (starts with emoji_)
          const isEncodedEmoji = reactionType.startsWith('emoji_')
          const displayEmoji = isEncodedEmoji ? emojiByType[reactionType] : null

          // For native emoji reactions (short strings that aren't predefined)
          const isNativeEmoji = reactionType.length <= 4 && !emojiObject && !isEncodedEmoji
          const count = reactionCounts?.[reactionType] || 0

          // Show if it's a predefined emoji, native emoji, or encoded emoji
          return emojiObject || isNativeEmoji || displayEmoji ? (
            <li
              className={clsx('str-chat__simple-reactions-list-item', {
                'str-chat__message-reaction-own': isOwnReaction,
              })}
              key={`${emojiObject?.id || reactionType}-${i}`}
              onClick={(event) => handleChipClick(reactionType, event)}
              onKeyUp={(event) => handleChipClick(reactionType, event)}
              onMouseEnter={(e) => {
                setHoveredReactionType(reactionType)
                setHoveredElement(e.currentTarget as HTMLElement)
              }}
            >
              <span>
                {displayEmoji ? (
                  // Display emoji from encoded reaction
                  <span className="text-base">{displayEmoji}</span>
                ) : isNativeEmoji ? (
                  // Display native emoji directly
                  <span className="text-base">{reactionType}</span>
                ) : (
                  // Display custom emoji using emoji-mart
                  <Suspense fallback={null}>
                    <Emoji
                      data={emojiData}
                      emoji={emojiObject}
                      size={13}
                      {...additionalEmojiProps}
                    />
                  </Suspense>
                )}
              </span>
              {count > 0 && <span className="ml-1 text-xs font-semibold">{count}</span>}
            </li>
          ) : null
        })}
      </ul>
      {hoveredReactionType && (
        <ReactionUsersPopup
          userNames={aggregatedUserNamesByType[hoveredReactionType] || []}
          reactionEmoji={getEmojiCharacter(hoveredReactionType)}
          isVisible={!!hoveredReactionType}
          referenceElement={hoveredElement}
        />
      )}
    </div>
  )
}

export const SimpleReactionsList = React.memo(
  UnMemoizedSimpleReactionsList
) as typeof UnMemoizedSimpleReactionsList
